import ComparePage, { CompareRow, CompareFaq, Reason } from '@comp/compare/compare-template'
import AssetDownload from '@comp/product/asset-download'

interface PageContent {
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  reasons: Reason[]
  tableTitle: [string, string]
  tableBody: string
  rows: CompareRow[]
  faqs: CompareFaq[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    seoTitle: 'Delinea Alternative: Monofor | PAM plus identity, one platform',
    seoDescription:
      'Looking for a Delinea alternative? Monopam covers vault, session recording, and just-in-time access, with SSO, MFA, and governance in the same platform, at per-session pricing.',
    heroEyebrow: 'Delinea alternative',
    heroTitle: ['The vault is table stakes.', 'The platform is the difference.'],
    heroBody:
      'Monopam matches the PAM core: encrypted vault, rotation, session recording, just-in-time approvals. Then the platform keeps going: SSO for 7,000+ apps, MFA, lifecycle, and access reviews in the same console, at per-concurrent-session pricing.',
    reasons: [
      {
        title: 'PAM that comes with identity',
        body: 'Delinea is a PAM specialist. Monofor covers PAM and the rest of identity (SSO, MFA, lifecycle, governance), so privileged access policy and workforce identity live in one console instead of two vendors.',
      },
      {
        title: 'Sessions, not seats',
        body: 'Monopam is licensed per concurrent privileged session. Admins, targets, and vaulted credentials are unlimited; the bill tracks actual privileged activity, not your org chart.',
      },
      {
        title: 'No agent fleet, no rollout project',
        body: 'Browser-based RDP, SSH, and database sessions mean day-one access from any machine. Resource discovery pulls from AD, Azure, and AWS automatically.',
      },
    ],
    tableTitle: ['Monofor vs Delinea,', 'feature by feature.'],
    tableBody:
      'At the vault the products are at parity. The gap opens at platform scope (identity and governance in the box) and at how the license is counted.',
    rows: [
      {
        feature: 'Privileged credential vault',
        detail: 'Encrypted storage, credential history, rotation',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Session recording',
        detail: 'Video + keystroke transcript for RDP & SSH',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Just-in-time privileged access',
        detail: 'Time-bound, approval-gated grants',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'IAM + IGA in the same platform',
        detail: 'SSO, MFA, lifecycle, access reviews in one console',
        monofor: true,
        competitor: 'PAM-focused',
      },
      {
        feature: 'Enterprise SSO app catalog',
        detail: '7,000+ pre-built SAML / OIDC / browser apps',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Legacy protocol bridges',
        detail: 'RADIUS server, LDAP gateway, header-based apps',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Self-hosted deployment',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Per-concurrent-session PAM pricing',
        detail: 'Not per admin, per target, or per credential',
        monofor: true,
        competitor: 'Per-user / quote-based',
      },
      {
        feature: 'Public entry pricing',
        detail: 'IAM from $4 per user / month',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Kubernetes & OpenShift access (native kubectl / oc)',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Database sessions with SQL policy & dynamic masking',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Certificate lifecycle management (CLM) built in',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Customer support included',
        monofor: true,
        competitor: 'Tiered',
      },
    ],
    faqs: [
      {
        q: 'How does Monofor compare to Delinea Secret Server?',
        a: 'On the PAM core (vaulting, rotation, session recording, just-in-time approvals), the products cover the same ground. The difference is scope and pricing: Monofor ships identity (SSO, MFA, lifecycle) and governance in the same platform, and licenses PAM per concurrent session rather than per user.',
      },
      {
        q: 'We already use Delinea for the vault. Can we add Monofor identity around it?',
        a: 'Yes. Monosign runs alongside any PAM tool; it federates via SAML/OIDC and can front Delinea consoles with SSO and MFA. Many teams start there, then consolidate the vault into Monopam at renewal time to drop a vendor.',
      },
      {
        q: 'What does migration involve?',
        a: 'Credentials import into the Monopam vault, and resources are discovered from Active Directory, Azure, and AWS. Session access is browser-based, so there is no agent fleet or client rollout to plan.',
      },
      {
        q: 'Is Monofor suitable for mid-market teams?',
        a: 'That is the sweet spot. Deployment takes minutes, not a services engagement; support is included rather than tiered; and per-concurrent-session licensing keeps the bill proportional to actual privileged activity.',
      },
    ],
  },
  tr: {
    seoTitle: 'Delinea Alternatifi: Monofor | PAM artı kimlik, tek platform',
    seoDescription:
      'Delinea alternatifi mi arıyorsunuz? Monopam; kasa, oturum kaydı ve tam zamanında erişimi (SSO, MFA ve yönetişim aynı platformda, oturum başına fiyatlandırmayla) sunar.',
    heroEyebrow: 'Delinea alternatifi',
    heroTitle: ['Kasa artık asgari standart.', 'Farkı yaratan platform.'],
    heroBody:
      'Monopam, PAM çekirdeğini karşılar: şifreli kasa, rotasyon, oturum kaydı, tam zamanında onaylar. Sonra platform devam eder: 7.000+ uygulama için SSO, MFA, yaşam döngüsü ve erişim gözden geçirmeleri aynı konsolda, eşzamanlı oturum başına fiyatlandırmayla.',
    reasons: [
      {
        title: 'Kimlikle birlikte gelen PAM',
        body: 'Delinea bir PAM uzmanıdır. Monofor, PAM ile birlikte kimliğin geri kalanını da kapsar (SSO, MFA, yaşam döngüsü, yönetişim); böylece ayrıcalıklı erişim politikası ve çalışan kimliği iki tedarikçi yerine tek konsolda yaşar.',
      },
      {
        title: 'Koltuk değil, oturum',
        body: 'Monopam eşzamanlı ayrıcalıklı oturum başına lisanslanır. Yöneticiler, hedefler ve kasadaki kimlik bilgileri sınırsızdır; fatura organizasyon şemanızı değil, gerçek ayrıcalıklı etkinliği takip eder.',
      },
      {
        title: 'Ajan filosu yok, dağıtım projesi yok',
        body: 'Tarayıcı tabanlı RDP, SSH ve veritabanı oturumları, ilk günden her makineden erişim demektir. Kaynak keşfi AD, Azure ve AWS’den otomatik olarak beslenir.',
      },
    ],
    tableTitle: ['Monofor ve Delinea,', 'özellik özellik.'],
    tableBody:
      'Kasada ürünler başa baş. Fark; platform kapsamında (kimlik ve yönetişimin pakete dahil olmasında) ve lisansın nasıl sayıldığında açılıyor.',
    rows: [
      {
        feature: 'Ayrıcalıklı kimlik bilgisi kasası',
        detail: 'Şifreli depolama, kimlik bilgisi geçmişi, rotasyon',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Oturum kaydı',
        detail: 'RDP ve SSH için video + tuş vuruşu dökümü',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Tam zamanında ayrıcalıklı erişim',
        detail: 'Süre sınırlı, onaya bağlı yetkilendirmeler',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'IAM + IGA aynı platformda',
        detail: 'SSO, MFA, yaşam döngüsü, erişim gözden geçirmeleri tek konsolda',
        monofor: true,
        competitor: 'PAM odaklı',
      },
      {
        feature: 'Kurumsal SSO uygulama kataloğu',
        detail: '7.000+ hazır SAML / OIDC / tarayıcı uygulaması',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Eski protokol köprüleri',
        detail: 'RADIUS sunucusu, LDAP ağ geçidi, header tabanlı uygulamalar',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Kendi altyapınızda kurulum',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Eşzamanlı oturum başına PAM fiyatlandırması',
        detail: 'Yönetici, hedef veya kimlik bilgisi başına değil',
        monofor: true,
        competitor: 'Kullanıcı başına / teklife dayalı',
      },
      {
        feature: 'Açık başlangıç fiyatı',
        detail: 'IAM kullanıcı başına aylık 4 $’dan başlar',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Kubernetes ve OpenShift erişimi (yerel kubectl / oc)',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'SQL politikası ve dinamik maskelemeli veritabanı oturumları',
        monofor: true,
        competitor: 'partial',
      },
      {
        feature: 'Yerleşik sertifika yaşam döngüsü yönetimi (CLM)',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Müşteri desteği dahil',
        monofor: true,
        competitor: 'Kademeli',
      },
    ],
    faqs: [
      {
        q: 'Monofor, Delinea Secret Server ile nasıl karşılaştırılır?',
        a: 'PAM çekirdeğinde (kasa, rotasyon, oturum kaydı, tam zamanında onaylar) ürünler aynı alanı kapsıyor. Fark kapsam ve fiyatlandırmada: Monofor kimliği (SSO, MFA, yaşam döngüsü) ve yönetişimi aynı platformda sunar ve PAM’i kullanıcı başına değil, eşzamanlı oturum başına lisanslar.',
      },
      {
        q: 'Kasa için zaten Delinea kullanıyoruz. Etrafına Monofor kimliğini ekleyebilir miyiz?',
        a: 'Evet. Monosign herhangi bir PAM aracıyla yan yana çalışır; SAML/OIDC üzerinden federasyon kurar ve Delinea konsollarının önüne SSO ve MFA koyabilir. Birçok ekip buradan başlar, ardından yenileme döneminde kasayı Monopam’e taşıyarak bir tedarikçiyi devreden çıkarır.',
      },
      {
        q: 'Geçiş neleri içerir?',
        a: 'Kimlik bilgileri Monopam kasasına aktarılır; kaynaklar Active Directory, Azure ve AWS’den keşfedilir. Oturum erişimi tarayıcı tabanlıdır, dolayısıyla planlanacak bir ajan filosu veya istemci dağıtımı yoktur.',
      },
      {
        q: 'Monofor orta ölçekli ekipler için uygun mu?',
        a: 'Tam da ideal olduğu nokta bu. Kurulum bir hizmet projesi değil, dakikalar alır; destek kademeli değil, dahildir; eşzamanlı oturum başına lisanslama ise faturayı gerçek ayrıcalıklı etkinlikle orantılı tutar.',
      },
    ],
  },
}

type Props = { locale: string }

export default function DelineaAlternativePage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <ComparePage
      leadMagnet={
        <AssetDownload
          asset="pam-rfp"
          title={{
            en: 'Evaluating PAM vendors? Take the 50 questions.',
            tr: 'PAM tedarikçisi mi değerlendiriyorsunuz? 50 soruyu alın.',
          }}
          copy={{
            en: 'A vendor-neutral RFP template: 50 questions across vault, sessions, JIT, Kubernetes, databases, certificates, EPM, architecture, integrations, and commercials, with a scoring rubric and the red flags to watch for.',
            tr: 'Tedarikçi-bağımsız RFP şablonu: kasa, oturumlar, JIT, Kubernetes, veritabanları, sertifikalar, EPM, mimari, entegrasyonlar ve ticari koşullarda 50 soru; puanlama cetveli ve dikkat edilecek kırmızı bayraklarla.',
          }}
        />
      }
      competitor="Delinea"
      locale={locale}
      bilingual
      path="/compare/delinea-alternative"
      seoTitle={c.seoTitle}
      seoDescription={c.seoDescription}
      heroEyebrow={c.heroEyebrow}
      heroTitle={
        <>
          {c.heroTitle[0]}
          <br />
          {c.heroTitle[1]}
        </>
      }
      heroBody={c.heroBody}
      reasons={c.reasons}
      tableTitle={
        <>
          {c.tableTitle[0]}
          <br />
          {c.tableTitle[1]}
        </>
      }
      tableBody={c.tableBody}
      rows={c.rows}
      faqs={c.faqs}
    />
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
